import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import React, {useMemo, useState} from "react";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import {Chip} from "@mui/material";
import LiteracyTestItems from "./LiteracyTestItems";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import TestItemCard from "../../../../shared/ui/TestItemCard";

interface IQuestion {
    id?: string | number;
    is_right_answer?: boolean;
    is_not_answered?: boolean;
    [key: string]: any;
}

interface ITestItem {
    id?: string | number;
    questions: IQuestion[];
    is_right_answer?: boolean;
    is_not_answered?: boolean;
    [key: string]: any;
}

interface IAnswersMenuProps {
    items: any[];
    testModeId: number;
    locationUrl: string;
    testId: string;
    totalItems: number;
    noAnswers: number;
    rightAnswers: number;
    wrongAnswers: number;
    notPayingAttention: number;
    isPrinting: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

interface TabConfig {
    index: number;
    title: string;
    count: number;
    filterStandard?: (item: ITestItem) => boolean;
    filterLiteracy?: (question: IQuestion) => boolean;
}

const filterItemsByQuestionCondition = (items: ITestItem[], predicate: (question: IQuestion) => boolean) => {
    return items
        .map((item) => ({
            ...item,
            questions: item.questions.filter(predicate)
        }))
        .filter((item) => item.questions.length > 0);
};

const  CustomTabPanel = ({ children, value, index, ...other }: TabPanelProps) => {

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <>{children}</>}
        </div>
    );
}

const a11yProps = (index: number) => {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const TabLabel = ({ itemsNr, tabTitle }: { itemsNr: number; tabTitle: string }) => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body1" mb={0} mr={1}>
            {tabTitle}
        </Typography>
        <Chip
            label={itemsNr}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: "0.75rem", height: 22, mr: 1 }}
        />
    </Box>
);

const tabSx = {
    color: "secondary.light",
    "& :hover, & .active": "primary.main",
};

const AnswersMenu = ({
                         items,
                         testModeId,
                         locationUrl,
                         testId,
                         totalItems,
                         noAnswers,
                         rightAnswers,
                         wrongAnswers,
                         notPayingAttention,
                         isPrinting,
} : IAnswersMenuProps) => {
    const [value, setValue] = useState(0);
    const isAnswersView = locationUrl === "raspunsuri";
    const isLiteracyMode = testModeId === 5;
    const isCustomTestMode = testModeId === 7;
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const tabsConfig: TabConfig[] = useMemo(() => {
        const baseTab: TabConfig = {
            index: 0,
            title: "Toți",
            count: totalItems,
        };

        if (!isAnswersView) {
            return [baseTab];
        }

        return [
            baseTab,
            {
                index: 1,
                title: "Corecți",
                count: rightAnswers,
                filterStandard: (item) => Boolean(item.is_right_answer),
                filterLiteracy: (question) => Boolean(question.is_right_answer),
            },
            {
                index: 2,
                title: "Greșiți",
                count: wrongAnswers,
                filterStandard: (item) => !item.is_right_answer && !item.is_not_answered && !item.not_paying_atention,
                filterLiteracy: (question) => !question.is_right_answer && !question.is_not_answered && !question.not_paying_atention,
            },
            ...(!isCustomTestMode
                ? [
                    {
                        index: 3,
                        title: "Din neatenție",
                        count: notPayingAttention,
                        filterStandard: (item: any) => !item.is_right_answer && !item.is_not_answered && item.not_paying_atention,
                        filterLiteracy: (question: any) => !question.is_right_answer && !question.is_not_answered && question.not_paying_atention,
                    },
                ]
                : []),
            {
                index: 4,
                title: "Omiși",
                count: noAnswers,
                filterStandard: (item) => Boolean(item.is_not_answered),
                filterLiteracy: (question) => Boolean(question.is_not_answered),
            },
        ];
    }, [isAnswersView, totalItems, rightAnswers, wrongAnswers, noAnswers, isCustomTestMode]);

    const renderTabContent = (tab: TabConfig) => {

        if (isLiteracyMode) {
            const filteredStudentAnswers = tab.filterLiteracy
                ? filterItemsByQuestionCondition(items, tab.filterLiteracy)
                : items;

            return (
                <LiteracyTestItems
                    locationUrl={locationUrl}
                    studentAnswers={filteredStudentAnswers}
                    testId={testId}
                    isPrinting={isPrinting}
                />
            );
        }

        const filteredItems = tab.filterStandard ? items.filter(tab.filterStandard) : items;

        return (
            <>
                {filteredItems.map((item, index) => (
                    <TestItemCard
                        key={item.id ?? index}
                        question={item}
                        isCustomItemPreview={false}
                        testId={testId}
                        difficulty={item.difficulty}
                        isAnswerReport={isAnswersView}
                        isPrinting={isPrinting}
                    />
                ))}
            </>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="Answers Report"
                    variant="scrollable"
                    scrollButtons={!isSmUp}
                    allowScrollButtonsMobile
                >
                    {tabsConfig.map((tab) => (
                        <Tab
                            key={tab.index}
                            value={tab.index}
                            label={<TabLabel tabTitle={tab.title} itemsNr={tab.count} />}
                            {...a11yProps(tab.index)}
                            sx={tabSx}
                        />
                    ))}
                </Tabs>
            </Box>

            {tabsConfig.map((tab) => (
                <CustomTabPanel key={tab.index} value={value} index={tab.index}>
                    {renderTabContent(tab)}
                </CustomTabPanel>
            ))}

        </Box>
    )

}

export default AnswersMenu;